import { useRef, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { ClearAttributeMappingsPayload } from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { MappingActionsToolbarProps } from './MappingActionsToolbar.props';
import {
  aiMenuItemTextSx,
  createLabelsPrimarySplitButtonSx,
  createLabelsSplitGroupSx,
  createLabelsTextSx,
  dropdownChevronButtonSx,
  menuItemAiIconSx,
  popperSx,
  pushMappingsButtonSx,
  toolbarContainerSx,
} from './MappingActionsToolbar.styles';
import { XLSX_VIEWER_STRINGS } from './xlsx-viewer.contants';
import availableShortcuts, { ACTION_SHORTCUT } from '../../../ShortcutHelper/availableShortCuts';

const MappingActionsToolbar = ({
  documentReferenceId,
  activeSheetLabel,
  onPushMappings,
  onClearMappings,
  hasPendingAISuggestions,
  onAcceptAllAISuggestions,
  onDeclineAllAISuggestions,
  onReprocessDocument,
}: MappingActionsToolbarProps) => {
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingClearPayload, setPendingClearPayload] = useState<ClearAttributeMappingsPayload>();
  const anchorRef = useRef<HTMLButtonElement>(null);

  const shortCutText = availableShortcuts.find(
    item => item.action === ACTION_SHORTCUT.PUSH_ATTRIBUTE_MAPPINGS
  )?.combination;

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleOpenConfirmDialog = (payload?: ClearAttributeMappingsPayload) => {
    setPendingClearPayload(payload);
    setConfirmDialogOpen(true);
    setOpen(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setPendingClearPayload(undefined);
  };

  const handleConfirmClearMappings = () => {
    onClearMappings(pendingClearPayload);
    handleCloseConfirmDialog();
  };

  const handleCreateLabels = () => {
    onReprocessDocument?.();
    setOpen(false);
  };

  const handleAcceptAll = () => {
    onAcceptAllAISuggestions?.();
    setOpen(false);
  };

  const handleDeclineAll = () => {
    onDeclineAllAISuggestions?.();
    setOpen(false);
  };

  return (
    <>
      <Box sx={toolbarContainerSx}>
        {/* CREATE LABELS button with dropdown */}
        <Box sx={createLabelsSplitGroupSx}>
          <Button
            variant="text"
            size="small"
            onClick={handleCreateLabels}
            sx={createLabelsPrimarySplitButtonSx}
            aria-haspopup="menu">
            <Typography component="span" sx={createLabelsTextSx}>
              {XLSX_VIEWER_STRINGS.BUTTON.CREATE_LABELS}
            </Typography>
          </Button>
          <Button
            ref={anchorRef}
            variant="text"
            size="small"
            sx={dropdownChevronButtonSx}
            onClick={handleToggle}
            endIcon={<ArrowDropDownIcon />}
            aria-controls={open ? XLSX_VIEWER_STRINGS.ARIA.MENU_ID : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu">
            <></>
          </Button>
        </Box>

        {/* PUSH MAPPINGS simple button */}
        <Tooltip
          title={`${shortCutText?.macOS}(${XLSX_VIEWER_STRINGS.TOOLTIP.MAC}) or ${shortCutText?.windows}(${XLSX_VIEWER_STRINGS.TOOLTIP.WINDOWS})`}>
          <Button variant="contained" color="success" size="small" onClick={onPushMappings} sx={pushMappingsButtonSx}>
            {XLSX_VIEWER_STRINGS.BUTTON.PUSH_MAPPINGS}
          </Button>
        </Tooltip>
      </Box>

      {/* DROPDOWN MENU */}
      <Popper sx={popperSx} open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id={XLSX_VIEWER_STRINGS.ARIA.MENU_ID} autoFocusItem>
                  {/* AI section - only visible when there are pending AI suggestions */}
                  {hasPendingAISuggestions && [
                    <MenuItem key="accept-all" onClick={handleAcceptAll}>
                      <Box component="img" src="/images/scalar_ai.png" alt="AI" sx={menuItemAiIconSx} />
                      <Typography sx={aiMenuItemTextSx}>{XLSX_VIEWER_STRINGS.MENU.ACCEPT_ALL}</Typography>
                    </MenuItem>,
                    <MenuItem key="decline-all" onClick={handleDeclineAll}>
                      <Box component="img" src="/images/scalar_ai.png" alt="AI" sx={menuItemAiIconSx} />
                      <Typography sx={aiMenuItemTextSx}>{XLSX_VIEWER_STRINGS.MENU.DECLINE_ALL}</Typography>
                    </MenuItem>,
                    <Divider key="ai-divider" />,
                  ]}

                  {/* Clear section - always visible */}
                  <MenuItem
                    onClick={() =>
                      handleOpenConfirmDialog({
                        documentReferenceId,
                        sheet: `${activeSheetLabel}`,
                      })
                    }>
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET} {activeSheetLabel ? `(${activeSheetLabel})` : ''}{' '}
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET_SUFFIX}
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenConfirmDialog({ documentReferenceId })}>
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_DOCUMENT}
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenConfirmDialog()}>{XLSX_VIEWER_STRINGS.MENU.CLEAR_ALL}</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Confirmation dialog for clear actions */}
      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{XLSX_VIEWER_STRINGS.DIALOG.TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{XLSX_VIEWER_STRINGS.DIALOG.CONTENT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            {XLSX_VIEWER_STRINGS.DIALOG.CANCEL}
          </Button>
          <Button onClick={handleConfirmClearMappings} color="error" variant="contained">
            {XLSX_VIEWER_STRINGS.DIALOG.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MappingActionsToolbar;
